import { IQuery, useFirebaseData } from "../../@shared/hooks/useFirebaseData";
import { Collections } from "../../@core/constants/FirebasePaths";
import { useUserSessionStore } from "../../stores/UserSession.store";
import { Queries } from "../../@core/constants/Queries";
import { IService } from "../../@core/interfaces/IService";
import { DocumentReference } from "@firebase/firestore";
import { ILocation } from "../../@core/interfaces/ILocation";

export const useServices = (loadData = false, queries: IQuery[] = []) => {
  const user = useUserSessionStore((state) => state.user);
  const db = useFirebaseData<IService>({
    collectionPath: `${Collections.services._path}`,
    loadData,
    queries: [...Queries.trash, ...queries],
  });

  const createService = async (data: any) => {
    const { lastServiceId } = await db.updateSequenceData(`${Collections.settings._path}/service`, (settings) => ({ lastServiceId: (settings?.lastServiceId || 0) + 1 }));

    const doc = await db.add({
      title: data.title,
      description: data.description,
      price: +data.price,
      company: db.getReference(`users/${user.id}`),
      details: {
        duration: +data.duration,
        provider: db.getReference(`providers/${data.provider}`),
        location: db.getReference(`locations/${data.location}`)
      },
      available: true,
      lastServiceId
    });

    await handleUploadFiles(`service/${doc.id}`, doc.id, data.images);
  };

  const editService = async (id: string, data: any) => {
    await db.update(id, {
      title: data.title,
      description: data.description,
      price: +data.price,
      company: db.getReference(`users/${user.id}`),
      details: {
        duration: +data.duration,
        provider: db.getReference(`providers/${data.provider}`),
        location: db.getReference(`locations/${data.location}`)
      },
      available: true
    });

    if (data.images instanceof FileList) await handleUploadFiles(`service/${id}`, id, data.images);
  };

  const handleUploadFiles = async (path: string, docId: string, files: any) => {
    const promises = Object.keys(files).map(async key => {
      const file = files[key];
      const ref = `${path}/${key}`;
      return await db.uploadFile(file, ref);
    });

    const results = await Promise.all(promises);

    await db.update(docId, {
      images: results
    });
  };

  const updateService = async (id: string, data: Partial<IService>) => {
    await db.update(id, data);
  };

  const deleteService = async (id: string) => {
    await db.trash(id);
  };

  const populatedServices = async (data: IService[]): Promise<IService[]> => {
    return await Promise.all(
      data.map(async (service) => {
        const company = await db.populateByItem(service, ["company"]);
        const location = await db.docWithId(`${Collections.locations._path}/${service.details.location.id}`);
        return { ...company, details: { ...service.details, location } };
      })
    );
  };

  const getService = async (id: string) => {
    return await db.docWithId(`${Collections.services._path}/${id}`);
  };

  const getServices = async (ids: DocumentReference[]): Promise<IService[]> => {
    if (!ids || ids.length === 0) return [];

    return Promise.all(
      ids.map(async (id) => {
        return await db.docWithId<IService>(id.path);
      })
    );
  };

  const getFeaturedServices = async (limit: number = 4): Promise<IService[]> => {
    const featuredQuery: IQuery[] = [
      { type: "where", field: "trash", operator: "==", value: false },
      { type: "where", field: "featured", operator: "==", value: true },
      { type: "limit", value: limit }
    ];

    const featuredServices = await db.collectionWithIds<IService>(featuredQuery);
    if (!featuredServices || featuredServices.length === 0) return [];

    return await Promise.all(
      featuredServices.map(async (service) => {
        const location = service.details.location?.path
          ? await db.docWithId<ILocation>(service.details.location.path)
          : null;
        return { ...service, details: { ...service.details, location } };
      })
    );
  };

  return {
    services: db.data,
    createService,
    updateService,
    deleteService,
    fetched: db.fetched,
    populatedServices,
    getService,
    getReference: db.getReference,
    editService,
    getServices,
    getFeaturedServices
  };
};
